import React from 'react';
import { motion } from 'framer-motion';
import { Compass, ShieldCheck, Heart } from 'lucide-react';
import { MarketingLayout } from '../../components/marketing/MarketingLayout';
import { useLanguage } from '../../contexts/LanguageContext';

export default function MarketingAboutPage() {
  const { t } = useLanguage();

  const VALUES = [
    {
      key: 'curated',
      icon: Compass,
      title: t('about.values.curated.title'),
      body: t('about.values.curated.body'),
    },
    {
      key: 'trust',
      icon: ShieldCheck,
      title: t('about.values.trust.title'),
      body: t('about.values.trust.body'),
    },
    {
      key: 'hospitality',
      icon: Heart,
      title: t('about.values.hospitality.title'),
      body: t('about.values.hospitality.body'),
    },
  ];

  return (
    <MarketingLayout>
      <section style={{ maxWidth: 920, margin: '0 auto', padding: '72px 22px 32px' }}>
        <motion.p
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          style={{ margin: 0, fontSize: 12, letterSpacing: 1.6, textTransform: 'uppercase', color: '#E53935', fontWeight: 700 }}
        >
          {t('about.eyebrow')}
        </motion.p>
        <motion.h1
          initial={{ opacity: 0, y: 14 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.55, delay: 0.05 }}
          data-testid="marketing-about-title"
          style={{
            margin: '14px 0 0',
            fontFamily: "'Inria Serif', serif",
            fontSize: 'clamp(2.4rem, 5vw, 4.2rem)',
            lineHeight: 1.02,
            letterSpacing: '-0.02em',
            color: 'var(--mkt-text)',
          }}
        >
          {t('about.title')}
        </motion.h1>
        <motion.p
          initial={{ opacity: 0, y: 14 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.15 }}
          style={{
            margin: '24px 0 0',
            fontFamily: "'Manrope', sans-serif",
            fontSize: 18,
            lineHeight: 1.65,
            color: 'var(--mkt-text-muted)',
          }}
        >
          {t('about.p1')}
        </motion.p>
        <motion.p
          initial={{ opacity: 0, y: 14 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.2 }}
          style={{
            margin: '16px 0 0',
            fontFamily: "'Manrope', sans-serif",
            fontSize: 18,
            lineHeight: 1.65,
            color: 'var(--mkt-text-muted)',
          }}
        >
          {t('about.p2')}
        </motion.p>
      </section>

      <section style={{ maxWidth: 1180, margin: '0 auto', padding: '56px 22px 80px' }}>
        <h2
          data-testid="marketing-about-values-title"
          style={{
            margin: 0,
            fontFamily: "'Inria Serif', serif",
            fontSize: 'clamp(1.8rem, 3.2vw, 2.6rem)',
            color: 'var(--mkt-text)',
            letterSpacing: '-0.01em',
          }}
        >
          {t('about.values.title')}
        </h2>
        <div
          style={{
            marginTop: 28,
            display: 'grid',
            gap: 18,
            gridTemplateColumns: 'repeat(auto-fit, minmax(260px, 1fr))',
          }}
        >
          {VALUES.map((value, idx) => {
            const Icon = value.icon;
            return (
              <motion.article
                key={value.key}
                initial={{ opacity: 0, y: 16 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: '-60px' }}
                transition={{ duration: 0.5, delay: idx * 0.08 }}
                data-testid={`marketing-about-value-${idx}`}
                style={{
                  background: 'var(--mkt-card-bg)',
                  border: '1px solid var(--mkt-card-border)',
                  borderRadius: 24,
                  padding: '28px 26px',
                  boxShadow: 'var(--mkt-card-shadow)',
                }}
              >
                <div
                  style={{
                    width: 46,
                    height: 46,
                    borderRadius: 14,
                    background: 'var(--mkt-accent-red-surface)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}
                >
                  <Icon size={20} color="#E53935" />
                </div>
                <h3
                  style={{
                    margin: '18px 0 8px',
                    fontFamily: "'Inria Serif', serif",
                    fontSize: 22,
                    color: 'var(--mkt-text)',
                    letterSpacing: '-0.01em',
                  }}
                >
                  {value.title}
                </h3>
                <p style={{ margin: 0, fontFamily: "'Manrope', sans-serif", fontSize: 15, lineHeight: 1.65, color: 'var(--mkt-text-muted)' }}>
                  {value.body}
                </p>
              </motion.article>
            );
          })}
        </div>
      </section>
    </MarketingLayout>
  );
}
